import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Plus,
  RefreshCw,
  Search,
  Filter,
  Receipt,
  FileText,
  Send,
  Printer,
  Pencil,
  Trash2,
  X
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { TERM_OPTIONS } from "../../constants/assessments";
import { useTenant } from "../../context/TenantContext";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import "./FeesManagementSimple.css";

interface ClassItem {
  id: string;
  name: string;
}

interface FeeTemplate {
  id: number;
  name: string;
  classId: number | null;
  className?: string;
  academicYear: number;
  term: string;
  totalAmount: number;
  items?: string;
  billingFrequency?: string;
  billingScope?: string;
  feeCategory?: string | null;
  sectionName?: string | null;
  transportRouteId?: number | null;
  transportRouteName?: string | null;
  isMandatory?: number | boolean;
  automaticBilling?: number | boolean;
  invoiceable?: number | boolean;
  isActive?: number | boolean;
}

interface InvoiceRow {
  id: number;
  studentId: number;
  studentName: string;
  classId: number | null;
  className?: string | null;
  academicYear: number;
  term: string;
  totalAmount: number;
  paid: number;
  balance: number;
  status: string;
  billingSource?: string;
  billingPeriod?: string | null;
  createdAt?: string;
}

interface FeeDetails {
  id: number;
  studentId: number;
  studentName?: string;
  classId: number;
  className?: string;
  academicYear: number;
  term: string;
  totalAmount: number;
  amountPaid?: number;
  balance: number;
  status: string;
  billingSource?: string;
  billingPeriod?: string | null;
}

interface FeeItem {
  id: number;
  studentFeeId: number;
  itemName: string;
  amount: number;
  quantity: number;
  total: number;
  billingSource?: string;
  invoiceable?: number | boolean;
  isMandatory?: number | boolean;
}

interface FeePayment {
  id: number;
  studentFeeId: number;
  amount: number;
  paymentMethod?: string;
  reference?: string;
  receiptNumber?: string;
  paidAt?: string;
}

interface InvoicePrintPayload {
  fee: FeeDetails | null;
  items: FeeItem[];
  payments: FeePayment[];
}

interface ItemInput {
  name: string;
  amount: string;
  quantity: number;
}

interface StudentOption {
  id: number;
  name: string;
  admissionNumber?: string;
}

interface TransportRouteOption {
  id: number;
  name: string;
  fare?: number;
}

const emptyItem = (): ItemInput => ({ name: "", amount: "", quantity: 1 });

const TABS = [
  { key: "unpaid", label: "Unpaid Fees" },
  { key: "types", label: "Fee Types / Fee Structure" },
  { key: "invoices", label: "Invoice Records" },
  { key: "charges", label: "Payment / Charge Setup" }
] as const;

type TabKey = (typeof TABS)[number]["key"];

const FeesManagementSimple: React.FC = () => {
  const { tenant } = useTenant();
  const token = localStorage.getItem("token") || "";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canManage = role === "admin" || role === "headteacher" || role === "superadmin";

  const [activeTab, setActiveTab] = useState<TabKey>("unpaid");
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [transportRoutes, setTransportRoutes] = useState<TransportRouteOption[]>([]);
  const [templates, setTemplates] = useState<FeeTemplate[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterTerm, setFilterTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<FeeTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: "",
    classId: "",
    academicYear: new Date().getFullYear().toString(),
    term: TERM_OPTIONS[0],
    billingFrequency: "termly",
    billingScope: "class",
    feeCategory: "School Fees",
    sectionName: "",
    transportRouteId: "",
    isMandatory: true,
    automaticBilling: true,
    invoiceable: true,
    isActive: true
  });
  const [templateItems, setTemplateItems] = useState<ItemInput[]>([emptyItem()]);
  const [manualAssignModalOpen, setManualAssignModalOpen] = useState(false);
  const [manualFeeForm, setManualFeeForm] = useState({
    studentId: "",
    classId: "",
    academicYear: new Date().getFullYear().toString(),
    term: TERM_OPTIONS[0],
    feeName: "",
    invoiceable: true,
    isMandatory: false
  });
  const [manualFeeItems, setManualFeeItems] = useState<ItemInput[]>([emptyItem()]);

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [feeDetails, setFeeDetails] = useState<FeeDetails | null>(null);
  const [feeItems, setFeeItems] = useState<FeeItem[]>([]);
  const [feePayments, setFeePayments] = useState<FeePayment[]>([]);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    studentFeeId: "",
    amount: "",
    paymentMethod: "Cash",
    reference: "",
    date: new Date().toISOString().slice(0, 10)
  });

  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat("en-ZM", { style: "currency", currency: "ZMW", maximumFractionDigits: 0 }).format(value || 0);

  const getItemsTotal = (items: ItemInput[]) =>
    items.reduce((sum, item) => sum + (Number(item.amount) || 0) * (Number(item.quantity) || 1), 0);

  const templateTotal = useMemo(() => getItemsTotal(templateItems), [templateItems]);

  const fetchClasses = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("academics/classes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classList = res.data?.classes || res.data || [];
      setClasses(classList.map((c: any) => ({ id: String(c.id), name: c.name })));
    } catch {
      setClasses([]);
    }
  }, [token]);

  const fetchTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("fees/template"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data?.templates || []);
    } catch {
      setTemplates([]);
    }
  }, [token]);

  const fetchStudents = useCallback(async () => {
    if (!token || !canManage) return;
    try {
      const res = await axios.get(getApiUrl("users/students"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studentList = res.data?.students || [];
      setStudents(studentList.map((student: any) => ({
        id: Number(student.id),
        name: student.name,
        admissionNumber: student.admissionNumber || student.userCode || ""
      })));
    } catch {
      setStudents([]);
    }
  }, [token, canManage]);

  const fetchTransportRoutes = useCallback(async () => {
    if (!token || !canManage) return;
    try {
      const res = await axios.get(getApiUrl("transport/routes"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransportRoutes(res.data?.routes || []);
    } catch {
      setTransportRoutes([]);
    }
  }, [token, canManage]);

  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setMessage(null);
    try {
      const params: Record<string, string> = {};
      if (filterClass) params.classId = filterClass;
      if (filterTerm) params.term = filterTerm;
      if (filterYear) params.year = filterYear;
      if (filterStatus !== "all") params.status = filterStatus;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const res = await axios.get(getApiUrl("fees/invoices"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInvoices(res.data?.invoices || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load invoices.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [token, filterClass, filterTerm, filterYear, filterStatus, searchTerm]);

  useEffect(() => {
    fetchClasses();
    fetchTemplates();
    fetchStudents();
    fetchTransportRoutes();
  }, [fetchClasses, fetchTemplates, fetchStudents, fetchTransportRoutes]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const unpaidInvoices = useMemo(() => invoices.filter((inv) => inv.balance > 0), [invoices]);

  const unpaidSummary = useMemo(() => {
    const totalUnpaid = unpaidInvoices.reduce((sum, inv) => sum + inv.balance, 0);
    const unpaidStudents = new Set(unpaidInvoices.map((inv) => inv.studentId)).size;
    const partial = unpaidInvoices.filter((inv) => inv.paid > 0).length;
    const fullyPaid = invoices.filter((inv) => inv.balance === 0).length;
    return { totalUnpaid, unpaidStudents, partial, fullyPaid };
  }, [unpaidInvoices, invoices]);

  const openTemplateModal = (template?: FeeTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name || "",
        classId: template.classId ? String(template.classId) : "",
        academicYear: String(template.academicYear || new Date().getFullYear()),
        term: template.term || TERM_OPTIONS[0],
        billingFrequency: template.billingFrequency || "termly",
        billingScope: template.billingScope || "class",
        feeCategory: template.feeCategory || "School Fees",
        sectionName: template.sectionName || "",
        transportRouteId: template.transportRouteId ? String(template.transportRouteId) : "",
        isMandatory: Boolean(Number(template.isMandatory ?? 1)),
        automaticBilling: Boolean(Number(template.automaticBilling ?? 1)),
        invoiceable: Boolean(Number(template.invoiceable ?? 1)),
        isActive: Boolean(Number(template.isActive ?? 1))
      });
      const parsed = template.items ? JSON.parse(template.items) : [];
      setTemplateItems(parsed.length ? parsed : [emptyItem()]);
    } else {
      setEditingTemplate(null);
      setTemplateForm({
        name: "",
        classId: "",
        academicYear: new Date().getFullYear().toString(),
        term: TERM_OPTIONS[0],
        billingFrequency: "termly",
        billingScope: "class",
        feeCategory: "School Fees",
        sectionName: "",
        transportRouteId: "",
        isMandatory: true,
        automaticBilling: true,
        invoiceable: true,
        isActive: true
      });
      setTemplateItems([emptyItem()]);
    }
    setTemplateModalOpen(true);
  };

  const saveTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManage) return;
    const normalizedItems = templateItems
      .map((item) => ({
        name: item.name.trim(),
        amount: Number(item.amount || 0),
        quantity: Number(item.quantity || 1)
      }))
      .filter((item) => item.name && item.amount > 0);

    if (!templateForm.name.trim() || normalizedItems.length === 0) {
      setMessage("Provide a fee title and at least one fee item.");
      return;
    }

    setLoading(true);
    try {
      if (editingTemplate) {
        await axios.put(
          getApiUrl(`fees/template/${editingTemplate.id}`),
          {
            ...templateForm,
            classId: templateForm.classId || null,
            transportRouteId: templateForm.transportRouteId || null,
            items: normalizedItems
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          getApiUrl("fees/template"),
          {
            ...templateForm,
            classId: templateForm.classId || null,
            transportRouteId: templateForm.transportRouteId || null,
            items: normalizedItems
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setTemplateModalOpen(false);
      fetchTemplates();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to save fee template.");
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (templateId: number) => {
    if (!canManage) return;
    if (!window.confirm("Disable this fee type?")) return;
    try {
      await axios.delete(getApiUrl(`fees/template/${templateId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTemplates();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to disable fee template.");
    }
  };

  const loadInvoiceDetails = useCallback(async (feeId: number): Promise<InvoicePrintPayload> => {
    const res = await axios.get(getApiUrl(`fees/fee/${feeId}`), {
      headers: { Authorization: `Bearer ${token}` }
    });
    return {
      fee: res.data?.fee || null,
      items: res.data?.items || [],
      payments: res.data?.payments || []
    };
  }, [token]);

  const openInvoice = async (feeId: number) => {
    if (!token) return;
    setLoading(true);
    try {
      const payload = await loadInvoiceDetails(feeId);
      setFeeDetails(payload.fee || null);
      setFeeItems(payload.items || []);
      setFeePayments(payload.payments || []);
      setInvoiceModalOpen(true);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load invoice details.");
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (feeId: number) => {
    const selectedInvoice = invoices.find((invoice) => invoice.id === feeId);
    if (selectedInvoice) {
      setFeeDetails((prev) => ({
        id: selectedInvoice.id,
        studentId: selectedInvoice.studentId,
        studentName: selectedInvoice.studentName,
        classId: Number(selectedInvoice.classId || 0),
        className: selectedInvoice.className || "",
        academicYear: selectedInvoice.academicYear,
        term: selectedInvoice.term,
        totalAmount: selectedInvoice.totalAmount,
        amountPaid: selectedInvoice.paid,
        balance: selectedInvoice.balance,
        status: selectedInvoice.status,
        billingSource: selectedInvoice.billingSource,
        billingPeriod: selectedInvoice.billingPeriod
      }));
    }
    setPaymentForm((prev) => ({
      ...prev,
      studentFeeId: String(feeId),
      amount: selectedInvoice?.balance ? String(selectedInvoice.balance) : prev.amount
    }));
    setPaymentModalOpen(true);
  };

  const recordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    try {
      await axios.post(
        getApiUrl("fees/payment"),
        {
          studentFeeId: paymentForm.studentFeeId,
          amount: paymentForm.amount,
          paymentMethod: paymentForm.paymentMethod,
          reference: paymentForm.reference,
          date: paymentForm.date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentModalOpen(false);
      fetchInvoices();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to record payment.");
    } finally {
      setLoading(false);
    }
  };

  const addItemRow = () => setTemplateItems((prev) => [...prev, emptyItem()]);
  const removeItemRow = (index: number) => setTemplateItems((prev) => prev.filter((_, i) => i !== index));
  const addManualItemRow = () => setManualFeeItems((prev) => [...prev, emptyItem()]);
  const removeManualItemRow = (index: number) => setManualFeeItems((prev) => prev.filter((_, i) => i !== index));
  const resetFilters = () => {
    setSearchTerm("");
    setFilterClass("");
    setFilterTerm("");
    setFilterStatus("all");
    setFilterYear(new Date().getFullYear().toString());
  };
  const manualFeeTotal = useMemo(() => getItemsTotal(manualFeeItems), [manualFeeItems]);

  const openManualAssignModal = () => {
    setManualFeeForm({
      studentId: "",
      classId: "",
      academicYear: filterYear || new Date().getFullYear().toString(),
      term: filterTerm || TERM_OPTIONS[0],
      feeName: "",
      invoiceable: true,
      isMandatory: false
    });
    setManualFeeItems([emptyItem()]);
    setManualAssignModalOpen(true);
  };

  const runAutoBilling = async () => {
    if (!token || !canManage) return;
    setLoading(true);
    setMessage(null);
    try {
      const res = await axios.post(
        getApiUrl("fees/billing/run"),
        {
          academicYear: filterYear || undefined,
          term: filterTerm || undefined,
          classId: filterClass || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data?.message || "Auto billing completed.");
      fetchInvoices();
      fetchTemplates();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to run auto billing.");
    } finally {
      setLoading(false);
    }
  };

  const buildPrintHtml = (payload: InvoicePrintPayload) => {
    const fee = payload.fee;
    if (!fee) return "";

    const schoolName = tenant?.name || "School";
    const schoolLogo = tenant?.logo_url || "";
    const generatedAt = new Date().toLocaleString();
    const itemRows = payload.items.length > 0
      ? payload.items.map((item) => `
          <tr>
            <td>${item.itemName}</td>
            <td>${item.billingSource || "-"}</td>
            <td>${item.quantity}</td>
            <td>${formatCurrency(item.amount)}</td>
            <td>${formatCurrency(item.total)}</td>
          </tr>
        `).join("")
      : `<tr><td colspan="5" class="empty">No billed items found.</td></tr>`;
    const paymentRows = payload.payments.length > 0
      ? payload.payments.map((payment) => `
          <tr>
            <td>${payment.receiptNumber || payment.reference || "-"}</td>
            <td>${payment.paymentMethod || "-"}</td>
            <td>${payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : "-"}</td>
            <td>${formatCurrency(payment.amount)}</td>
          </tr>
        `).join("")
      : `<tr><td colspan="4" class="empty">No payments recorded.</td></tr>`;

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${schoolName} Fee Statement</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, sans-serif; background: #eef2f7; color: #0f172a; }
    .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 18mm 16mm; }
    .header { display: flex; justify-content: space-between; align-items: center; gap: 18px; padding-bottom: 16px; border-bottom: 2px solid #0f766e; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .logo { width: 68px; height: 68px; border-radius: 16px; overflow: hidden; border: 1px solid #cbd5e1; display: flex; align-items: center; justify-content: center; background: #fff; }
    .logo img { width: 100%; height: 100%; object-fit: contain; }
    .logo-fallback { font-weight: 700; font-size: 20px; color: #0f766e; }
    .school h1 { margin: 0; font-size: 26px; }
    .school p { margin: 4px 0 0; color: #475569; font-size: 13px; }
    .doc-meta { text-align: right; }
    .doc-meta .pill { display: inline-block; padding: 8px 12px; border-radius: 999px; background: #ecfeff; color: #0f766e; font-weight: 700; font-size: 12px; text-transform: uppercase; }
    .doc-meta p { margin: 10px 0 0; font-size: 12px; color: #64748b; }
    .student-card, .metrics { display: grid; gap: 12px; }
    .student-card { grid-template-columns: repeat(4, 1fr); margin-top: 18px; padding: 16px; border: 1px solid #dbeafe; background: #f8fbff; border-radius: 16px; }
    .metrics { grid-template-columns: repeat(4, 1fr); margin-top: 18px; }
    .student-card span, .metric span { display: block; font-size: 11px; text-transform: uppercase; color: #64748b; margin-bottom: 6px; }
    .student-card strong { font-size: 15px; }
    .metric { border: 1px solid #e2e8f0; border-radius: 16px; padding: 14px; background: #fff; }
    .metric strong { font-size: 18px; }
    .section { margin-top: 24px; }
    .section h2 { margin: 0 0 10px; font-size: 15px; text-transform: uppercase; letter-spacing: 0.08em; color: #0f766e; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 12px; vertical-align: top; }
    th { background: #f8fafc; text-transform: uppercase; font-size: 11px; letter-spacing: 0.05em; color: #475569; }
    .empty { text-align: center; color: #64748b; }
    .footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #cbd5e1; font-size: 11px; color: #64748b; display: flex; justify-content: space-between; gap: 12px; }
    @media print {
      body { background: #fff; }
      .page { width: auto; min-height: auto; margin: 0; padding: 12mm; }
      @page { size: A4; margin: 10mm; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        <div class="logo">
          ${schoolLogo ? `<img src="${schoolLogo}" alt="${schoolName} logo" />` : `<div class="logo-fallback">${schoolName.slice(0, 2).toUpperCase()}</div>`}
        </div>
        <div class="school">
          <h1>${schoolName}</h1>
          <p>Official fee invoice and payment statement</p>
        </div>
      </div>
      <div class="doc-meta">
        <div class="pill">Fees Statement</div>
        <p>Printed: ${generatedAt}</p>
        <p>Billing period: ${fee.billingPeriod || `${fee.term} ${fee.academicYear}`}</p>
      </div>
    </div>

    <div class="student-card">
      <div><span>Learner</span><strong>${fee.studentName || "N/A"}</strong></div>
      <div><span>Class</span><strong>${fee.className || "N/A"}</strong></div>
      <div><span>Academic Term</span><strong>${fee.term} ${fee.academicYear}</strong></div>
      <div><span>Billing Source</span><strong>${fee.billingSource || "mixed"}</strong></div>
    </div>

    <div class="metrics">
      <div class="metric"><span>Total billed</span><strong>${formatCurrency(fee.totalAmount)}</strong></div>
      <div class="metric"><span>Total paid</span><strong>${formatCurrency(fee.amountPaid || 0)}</strong></div>
      <div class="metric"><span>Outstanding balance</span><strong>${formatCurrency(fee.balance)}</strong></div>
      <div class="metric"><span>Status</span><strong>${fee.status}</strong></div>
    </div>

    <div class="section">
      <h2>Billed Items</h2>
      <table>
        <thead>
          <tr><th>Item</th><th>Source</th><th>Qty</th><th>Amount</th><th>Total</th></tr>
        </thead>
        <tbody>${itemRows}</tbody>
      </table>
    </div>

    <div class="section">
      <h2>Payment History</h2>
      <table>
        <thead>
          <tr><th>Receipt / Ref</th><th>Method</th><th>Date</th><th>Amount</th></tr>
        </thead>
        <tbody>${paymentRows}</tbody>
      </table>
    </div>

    <div class="footer">
      <span>This document was generated by the school fees office.</span>
      <span>School header and logo are included for official print use.</span>
    </div>
  </div>
</body>
</html>`;
  };

  const printInvoice = async (feeId: number) => {
    if (!token) return;
    setLoading(true);
    setMessage(null);
    try {
      const payload = await loadInvoiceDetails(feeId);
      const printWindow = window.open("", "_blank", "width=1024,height=768");
      if (!printWindow) {
        setMessage("Popup blocked while opening the printable fee statement.");
        return;
      }
      printWindow.document.open();
      printWindow.document.write(buildPrintHtml(payload));
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to prepare printable statement.");
    } finally {
      setLoading(false);
    }
  };

  const assignManualFee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !canManage) return;
    const normalizedItems = manualFeeItems
      .map((item) => ({
        name: item.name.trim(),
        amount: Number(item.amount || 0),
        quantity: Number(item.quantity || 1)
      }))
      .filter((item) => item.name && item.amount > 0);

    if (!manualFeeForm.studentId || !manualFeeForm.classId || normalizedItems.length === 0) {
      setMessage("Select a learner, class, and at least one manual fee item.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        getApiUrl("fees/student"),
        {
          ...manualFeeForm,
          feeName: manualFeeForm.feeName.trim(),
          items: normalizedItems
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setManualAssignModalOpen(false);
      setMessage("Manual fee assigned.");
      fetchInvoices();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to assign manual fee.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="utopia-page">
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title">Fees Management</h1>
          <p className="page-subtitle">Admin-managed fee structures, auto billing, transport mapping, and payment updates.</p>
        </div>
      </div>

      <div className="utopia-card p-4 mb-6">
        <div className="fees-topbar">
          <div className="fees-topbar-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-all ${
                  activeTab === tab.key
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="fees-topbar-actions">
            {canManage && (
              <button className="utopia-btn-primary flex items-center gap-2" onClick={runAutoBilling} disabled={loading}>
                <RefreshCw size={16} /> Run Auto Billing
              </button>
            )}
            <button className="utopia-btn-secondary flex items-center gap-2" onClick={fetchInvoices}>
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-6 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{message}</div>
      )}

      <div className="utopia-card p-4 mb-6">
        <div className="flex items-center gap-2 text-slate-600 text-sm font-bold mb-3">
          <Filter size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="utopia-input pl-8"
                placeholder="Student or ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Class</label>
            <select className="utopia-input" value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>{cls.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Term</label>
            <select className="utopia-input" value={filterTerm} onChange={(e) => setFilterTerm(e.target.value)}>
              <option value="">All Terms</option>
              {TERM_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Year</label>
            <input
              className="utopia-input"
              type="number"
              min="2000"
              max="2100"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Status</label>
            <select className="utopia-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="grid grid-cols-1 gap-2 w-full">
              <button className="utopia-btn-primary w-full" onClick={fetchInvoices}>Apply Filters</button>
              <button className="utopia-btn-secondary w-full" onClick={resetFilters}>Reset Filters</button>
            </div>
          </div>
        </div>
      </div>

      {activeTab === "unpaid" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="utopia-card p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Unpaid</p>
              <p className="text-2xl font-black text-amber-600">{formatCurrency(unpaidSummary.totalUnpaid)}</p>
            </div>
            <div className="utopia-card p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Unpaid Students</p>
              <p className="text-2xl font-black text-slate-800">{unpaidSummary.unpaidStudents}</p>
            </div>
            <div className="utopia-card p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Partial Payments</p>
              <p className="text-2xl font-black text-teal-600">{unpaidSummary.partial}</p>
            </div>
            <div className="utopia-card p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Fully Paid</p>
              <p className="text-2xl font-black text-emerald-600">{unpaidSummary.fullyPaid}</p>
            </div>
          </div>

          <div className="utopia-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold">Unpaid Fees</h3>
            </div>
            {unpaidInvoices.length === 0 ? (
              <div className="text-sm text-slate-400">No unpaid fees found for this period.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-2">Admission #</th>
                      <th className="py-2">Student Name</th>
                      <th className="py-2">Class</th>
                      <th className="py-2">Term</th>
                      <th className="py-2">Fee Balance</th>
                      <th className="py-2">Status</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unpaidInvoices.map((row) => (
                      <tr key={row.id} className="border-t border-slate-100">
                        <td className="py-2">{row.studentId}</td>
                        <td className="py-2 font-semibold">{row.studentName}</td>
                        <td className="py-2">{row.className || "-"}</td>
                        <td className="py-2">{row.term}</td>
                        <td className="py-2 text-amber-600 font-semibold">{formatCurrency(row.balance)}</td>
                        <td className="py-2">{row.status}</td>
                        <td className="py-2 text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            <button className="utopia-btn-secondary h-8" onClick={() => openInvoice(row.id)}>
                              <FileText size={14} /> View Invoice
                            </button>
                            <button className="utopia-btn-primary h-8" onClick={() => openPaymentModal(row.id)}>
                              <Receipt size={14} /> Pay
                            </button>
                            <button className="utopia-btn-secondary h-8" onClick={() => setMessage("Reminder queued for delivery.")}> 
                              <Send size={14} /> Send Reminder
                            </button>
                            <button className="utopia-btn-secondary h-8" onClick={() => printInvoice(row.id)}>
                              <Printer size={14} /> Print
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === "types" && (
        <div className="utopia-card p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold">Fee Types / Fee Structure</h3>
              <p className="text-sm text-slate-500">Create, edit, or disable fee structures per class.</p>
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2">
                <button className="utopia-btn-secondary flex items-center gap-2" onClick={openManualAssignModal}>
                  <Plus size={16} /> Manual Fee
                </button>
                <button className="utopia-btn-primary flex items-center gap-2" onClick={() => openTemplateModal()}>
                <Plus size={16} /> Add New Fee Type
                </button>
              </div>
            )}
          </div>
          {templates.length === 0 ? (
            <div className="text-sm text-slate-400">No fee types found. Add a fee type to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2">Fee Title</th>
                    <th className="py-2">Class</th>
                    <th className="py-2">Billing Type</th>
                    <th className="py-2">Scope</th>
                    <th className="py-2">Invoiceable</th>
                    <th className="py-2">Total Amount</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tpl) => (
                    <tr key={tpl.id} className="border-t border-slate-100">
                      <td className="py-2 font-semibold">{tpl.name}</td>
                      <td className="py-2">{tpl.className || "All"}</td>
                      <td className="py-2">{tpl.billingFrequency || "termly"}</td>
                      <td className="py-2">{tpl.billingScope === "transport" ? tpl.transportRouteName || "Transport Route" : tpl.billingScope || "class"}</td>
                      <td className="py-2">{Number(tpl.invoiceable ?? 1) ? "Yes" : "No"}</td>
                      <td className="py-2">{formatCurrency(tpl.totalAmount)}</td>
                      <td className="py-2 text-right">
                        <div className="fees-table-actions">
                          <button className="utopia-btn-secondary fees-table-action-btn" onClick={() => openTemplateModal(tpl)}>
                            <Pencil size={14} /> Edit
                          </button>
                          <button className="utopia-btn-secondary fees-table-action-btn" onClick={() => deleteTemplate(tpl.id)}>
                            <Trash2 size={14} /> Disable
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "invoices" && (
        <div className="utopia-card p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold">Invoice Records</h3>
              <p className="text-sm text-slate-500">Generated invoices and balances.</p>
            </div>
          </div>
          {invoices.length === 0 ? (
            <div className="text-sm text-slate-400">No invoices found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2">Student</th>
                    <th className="py-2">Class</th>
                    <th className="py-2">Term</th>
                    <th className="py-2">Total</th>
                    <th className="py-2">Paid</th>
                    <th className="py-2">Balance</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Source</th>
                    <th className="py-2">Date</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="py-2 font-semibold">{row.studentName}</td>
                      <td className="py-2">{row.className || "-"}</td>
                      <td className="py-2">{row.term}</td>
                      <td className="py-2">{formatCurrency(row.totalAmount)}</td>
                      <td className="py-2">{formatCurrency(row.paid)}</td>
                      <td className="py-2">{formatCurrency(row.balance)}</td>
                      <td className="py-2">{row.status}</td>
                      <td className="py-2">{row.billingSource || "-"}</td>
                      <td className="py-2">{row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-"}</td>
                      <td className="py-2 text-right">
                        <button className="utopia-btn-secondary h-8" onClick={() => openInvoice(row.id)}>
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "charges" && (
        <div className="utopia-card p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold">Payment / Charge Setup</h3>
              <p className="text-sm text-slate-500">Configure charge items used for invoices and billing.</p>
            </div>
            {canManage && (
              <button className="utopia-btn-primary flex items-center gap-2" onClick={() => openTemplateModal()}>
                <Plus size={16} /> Add Charge
              </button>
            )}
          </div>
          {templates.length === 0 ? (
            <div className="text-sm text-slate-400">No charges configured. Add a charge to begin.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2">Charge Name</th>
                    <th className="py-2">Applies To</th>
                    <th className="py-2">Billing Cycle</th>
                    <th className="py-2">Source</th>
                    <th className="py-2">Invoiceable</th>
                    <th className="py-2">Amount</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tpl) => (
                    <tr key={tpl.id} className="border-t border-slate-100">
                      <td className="py-2 font-semibold">{tpl.name}</td>
                      <td className="py-2">{tpl.billingScope === "transport" ? tpl.transportRouteName || "Transport route" : tpl.className || "All"}</td>
                      <td className="py-2">{tpl.billingFrequency || "termly"}</td>
                      <td className="py-2">{tpl.billingScope || "class"}</td>
                      <td className="py-2">{Number(tpl.invoiceable ?? 1) ? "Yes" : "No"}</td>
                      <td className="py-2">{formatCurrency(tpl.totalAmount)}</td>
                      <td className="py-2 text-right">
                        <div className="fees-table-actions">
                          <button className="utopia-btn-secondary fees-table-action-btn" onClick={() => openTemplateModal(tpl)}>
                            <Pencil size={14} /> Edit
                          </button>
                          <button className="utopia-btn-secondary fees-table-action-btn" onClick={() => deleteTemplate(tpl.id)}>
                            <Trash2 size={14} /> Disable
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {templateModalOpen && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">{editingTemplate ? "Edit Fee Type" : "Add Fee Type"}</h3>
                <p className="text-sm text-slate-500">Configure class fees, transport charges, and recurring billing rules.</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setTemplateModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveTemplate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Fee Title</label>
                  <input
                    className="utopia-input"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Assessment Fee"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Class</label>
                  <select
                    className="utopia-input"
                    value={templateForm.classId}
                    disabled={templateForm.billingScope === "transport"}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, classId: e.target.value }))}
                  >
                    <option value="">All Classes</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Academic Year</label>
                  <input
                    className="utopia-input"
                    type="number"
                    min="2000"
                    max="2100"
                    value={templateForm.academicYear}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, academicYear: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Term</label>
                  <select
                    className="utopia-input"
                    value={templateForm.term}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, term: e.target.value }))}
                  >
                    {TERM_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Billing Frequency</label>
                  <select
                    className="utopia-input"
                    value={templateForm.billingFrequency}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, billingFrequency: e.target.value }))}
                  >
                    <option value="once">Once</option>
                    <option value="monthly">Monthly</option>
                    <option value="termly">Termly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Billing Scope</label>
                  <select
                    className="utopia-input"
                    value={templateForm.billingScope}
                    onChange={(e) =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        billingScope: e.target.value,
                        classId: e.target.value === "transport" ? "" : prev.classId
                      }))
                    }
                  >
                    <option value="class">Class-based</option>
                    <option value="transport">Transport route</option>
                    <option value="manual">Manual template</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Category</label>
                  <input
                    className="utopia-input"
                    value={templateForm.feeCategory}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, feeCategory: e.target.value }))}
                    placeholder="School Fees"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Section</label>
                  <input
                    className="utopia-input"
                    value={templateForm.sectionName}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, sectionName: e.target.value }))}
                    placeholder="Optional section"
                  />
                </div>
                {templateForm.billingScope === "transport" && (
                  <div className="md:col-span-2">
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Transport Route</label>
                    <select
                      className="utopia-input"
                      value={templateForm.transportRouteId}
                      onChange={(e) => setTemplateForm((prev) => ({ ...prev, transportRouteId: e.target.value }))}
                    >
                      <option value="">Select route</option>
                      {transportRoutes.map((route) => (
                        <option key={route.id} value={route.id}>
                          {route.name}{route.fare ? ` - ${formatCurrency(route.fare)}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="fees-template-flags">
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={templateForm.automaticBilling}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, automaticBilling: e.target.checked }))}
                  />
                  <span>Automatic billing</span>
                </label>
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={templateForm.invoiceable}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, invoiceable: e.target.checked }))}
                  />
                  <span>Invoiceable</span>
                </label>
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={templateForm.isMandatory}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, isMandatory: e.target.checked }))}
                  />
                  <span>Mandatory</span>
                </label>
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={templateForm.isActive}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-700">Fee Items</h4>
                  <button type="button" className="utopia-btn-secondary fees-table-action-btn" onClick={addItemRow}>
                    <Plus size={14} /> Add Item
                  </button>
                </div>
                <div className="space-y-3">
                  {templateItems.map((item, idx) => (
                    <div key={`item-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-5">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Item Name</label>
                        <input
                          className="utopia-input"
                          value={item.name}
                          onChange={(e) =>
                            setTemplateItems((prev) => prev.map((row, i) => (i === idx ? { ...row, name: e.target.value } : row)))
                          }
                          placeholder="Tuition"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Amount</label>
                        <input
                          className="utopia-input"
                          type="number"
                          min="0"
                          value={item.amount}
                          onChange={(e) =>
                            setTemplateItems((prev) => prev.map((row, i) => (i === idx ? { ...row, amount: e.target.value } : row)))
                          }
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Qty</label>
                        <input
                          className="utopia-input"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            setTemplateItems((prev) => prev.map((row, i) => (i === idx ? { ...row, quantity: Number(e.target.value) || 1 } : row)))
                          }
                        />
                      </div>
                      <div className="md:col-span-2 flex items-end gap-2">
                        <div className="text-sm font-semibold text-slate-600">
                          {formatCurrency((Number(item.amount) || 0) * (Number(item.quantity) || 1))}
                        </div>
                        {templateItems.length > 1 && (
                          <button
                            type="button"
                            className="utopia-btn-icon text-red-500"
                            onClick={() => removeItemRow(idx)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  Total: <span className="text-lg font-bold text-slate-800">{formatCurrency(templateTotal)}</span>
                  <span className="fees-inline-meta">
                    {templateForm.billingScope === "transport"
                      ? "Bills only learners assigned to the selected transport route."
                      : "Learner view remains read-only. Admin changes feed billing automatically."}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="utopia-btn-secondary" onClick={() => setTemplateModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="utopia-btn-primary" disabled={loading}>
                    {editingTemplate ? "Save Changes" : "Create Fee Type"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {invoiceModalOpen && feeDetails && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal wide">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">Invoice Details</h3>
                <p className="text-sm text-slate-500">{feeDetails.studentName || "Student"} · {feeDetails.term} {feeDetails.academicYear}</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setInvoiceModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="utopia-card p-3">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total</p>
                <p className="text-lg font-bold text-slate-800">{formatCurrency(feeDetails.totalAmount)}</p>
              </div>
              <div className="utopia-card p-3">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Paid</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(feeDetails.amountPaid || 0)}</p>
              </div>
              <div className="utopia-card p-3">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Balance</p>
                <p className="text-lg font-bold text-amber-600">{formatCurrency(feeDetails.balance)}</p>
              </div>
              <div className="utopia-card p-3">
                <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Status</p>
                <p className="text-lg font-bold text-slate-700">{feeDetails.status}</p>
              </div>
            </div>
            <div className="fees-detail-banner">
              <span>Billing period: {feeDetails.billingPeriod || `${feeDetails.term} ${feeDetails.academicYear}`}</span>
              <span>Source: {feeDetails.billingSource || "mixed"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-bold mb-2">Fee Items</h4>
                {feeItems.length === 0 ? (
                  <div className="text-sm text-slate-400">No fee items found.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="py-2">Item</th>
                          <th className="py-2">Qty</th>
                          <th className="py-2">Amount</th>
                          <th className="py-2">Total</th>
                          <th className="py-2">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeItems.map((item) => (
                          <tr key={item.id} className="border-t border-slate-100">
                            <td className="py-2 font-semibold">{item.itemName}</td>
                            <td className="py-2">{item.quantity}</td>
                            <td className="py-2">{formatCurrency(item.amount)}</td>
                            <td className="py-2">{formatCurrency(item.total)}</td>
                            <td className="py-2">{item.billingSource || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-sm font-bold mb-2">Payments</h4>
                {feePayments.length === 0 ? (
                  <div className="text-sm text-slate-400">No payments recorded yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500">
                          <th className="py-2">Amount</th>
                          <th className="py-2">Method</th>
                          <th className="py-2">Receipt</th>
                          <th className="py-2">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feePayments.map((pay) => (
                          <tr key={pay.id} className="border-t border-slate-100">
                            <td className="py-2 font-semibold">{formatCurrency(pay.amount)}</td>
                            <td className="py-2">{pay.paymentMethod || "-"}</td>
                            <td className="py-2">{pay.receiptNumber || pay.reference || "-"}</td>
                            <td className="py-2">{pay.paidAt ? new Date(pay.paidAt).toLocaleDateString() : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button className="utopia-btn-secondary" onClick={() => setInvoiceModalOpen(false)}>
                Close
              </button>
              <button className="utopia-btn-secondary" onClick={() => printInvoice(feeDetails.id)}>
                <Printer size={14} /> Print Statement
              </button>
              {feeDetails.balance > 0 && (
                <button className="utopia-btn-primary" onClick={() => openPaymentModal(feeDetails.id)}>
                  Record Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {paymentModalOpen && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">Record Payment</h3>
                <p className="text-sm text-slate-500">Apply a payment to the selected invoice.</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setPaymentModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={recordPayment}>
              {feeDetails && (
                <div className="fees-payment-summary">
                  <div>
                    <span>Total billed</span>
                    <strong>{formatCurrency(feeDetails.totalAmount)}</strong>
                  </div>
                  <div>
                    <span>Total paid</span>
                    <strong>{formatCurrency(feeDetails.amountPaid || 0)}</strong>
                  </div>
                  <div>
                    <span>Balance</span>
                    <strong>{formatCurrency(feeDetails.balance)}</strong>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Amount</label>
                  <input
                    className="utopia-input"
                    type="number"
                    min="0"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Payment Method</label>
                  <select
                    className="utopia-input"
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Mobile">Mobile Money</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Reference</label>
                  <input
                    className="utopia-input"
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, reference: e.target.value }))}
                    placeholder="Receipt # or ref"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Payment Date</label>
                  <input
                    className="utopia-input"
                    type="date"
                    value={paymentForm.date}
                    onChange={(e) => setPaymentForm((prev) => ({ ...prev, date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" className="utopia-btn-secondary" onClick={() => setPaymentModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary" disabled={loading}>
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {manualAssignModalOpen && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">Manual Fee Assignment</h3>
                <p className="text-sm text-slate-500">Add a one-off learner charge without altering the automatic fee structure.</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setManualAssignModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={assignManualFee}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Learner</label>
                  <select
                    className="utopia-input"
                    value={manualFeeForm.studentId}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, studentId: e.target.value }))}
                    required
                  >
                    <option value="">Select learner</option>
                    {students.map((student) => (
                      <option key={student.id} value={student.id}>
                        {student.name}{student.admissionNumber ? ` (${student.admissionNumber})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Class</label>
                  <select
                    className="utopia-input"
                    value={manualFeeForm.classId}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, classId: e.target.value }))}
                    required
                  >
                    <option value="">Select class</option>
                    {classes.map((cls) => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Fee Title</label>
                  <input
                    className="utopia-input"
                    value={manualFeeForm.feeName}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, feeName: e.target.value }))}
                    placeholder="Exam re-entry fee"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Academic Year</label>
                  <input
                    className="utopia-input"
                    type="number"
                    min="2000"
                    max="2100"
                    value={manualFeeForm.academicYear}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, academicYear: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Term</label>
                  <select
                    className="utopia-input"
                    value={manualFeeForm.term}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, term: e.target.value }))}
                  >
                    {TERM_OPTIONS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="fees-template-flags">
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={manualFeeForm.invoiceable}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, invoiceable: e.target.checked }))}
                  />
                  <span>Invoiceable</span>
                </label>
                <label className="fees-checkbox-row">
                  <input
                    type="checkbox"
                    checked={manualFeeForm.isMandatory}
                    onChange={(e) => setManualFeeForm((prev) => ({ ...prev, isMandatory: e.target.checked }))}
                  />
                  <span>Mandatory</span>
                </label>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-bold text-slate-700">Manual Fee Items</h4>
                  <button type="button" className="utopia-btn-secondary fees-table-action-btn" onClick={addManualItemRow}>
                    <Plus size={14} /> Add Item
                  </button>
                </div>
                <div className="space-y-3">
                  {manualFeeItems.map((item, idx) => (
                    <div key={`manual-item-${idx}`} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                      <div className="md:col-span-5">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Item Name</label>
                        <input
                          className="utopia-input"
                          value={item.name}
                          onChange={(e) =>
                            setManualFeeItems((prev) => prev.map((row, i) => (i === idx ? { ...row, name: e.target.value } : row)))
                          }
                          placeholder="Special activity fee"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Amount</label>
                        <input
                          className="utopia-input"
                          type="number"
                          min="0"
                          value={item.amount}
                          onChange={(e) =>
                            setManualFeeItems((prev) => prev.map((row, i) => (i === idx ? { ...row, amount: e.target.value } : row)))
                          }
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Qty</label>
                        <input
                          className="utopia-input"
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            setManualFeeItems((prev) => prev.map((row, i) => (i === idx ? { ...row, quantity: Number(e.target.value) || 1 } : row)))
                          }
                        />
                      </div>
                      <div className="md:col-span-2 flex items-end gap-2">
                        <div className="text-sm font-semibold text-slate-600">
                          {formatCurrency((Number(item.amount) || 0) * (Number(item.quantity) || 1))}
                        </div>
                        {manualFeeItems.length > 1 && (
                          <button type="button" className="utopia-btn-icon text-red-500" onClick={() => removeManualItemRow(idx)}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  Total: <span className="text-lg font-bold text-slate-800">{formatCurrency(manualFeeTotal)}</span>
                </div>
                <div className="flex gap-2">
                  <button type="button" className="utopia-btn-secondary" onClick={() => setManualAssignModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="utopia-btn-primary" disabled={loading}>
                    Assign Fee
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesManagementSimple;

